import {
  NO_LIMIT,
  REMBLE_API_URL,
  REMBLE_WP_API_URL,
  learnDashGET,
  postRESTParams
} from '.'
import { getAuthenticatedUser } from '../../auth'
import {
  LMS_TYPE_TOPIC,
  LMS_TYPE_PREFIX,
  tryGetTopicDataForLessonFromCourseSteps
} from './Courses'

const STATUS_IN_PROGRESS = 'in-progress'
const STATUS_STARTED = 'not-started'
const STATUS_COMPLETED = 'completed'

const ACTION_UPDATE = 'update'
const ACTION_COMPLETE = 'complete'

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Common Course progress POST request pattern wrapper.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _progressRESTPOST = async (url, params) => {
  const result = await fetch(url, postRESTParams(params))
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
      return { error }
    })

  return result
}

/**
 * Helper function to get the progress object for the given course object, if one exists
 * @param {Array} courseProgress The course progress array to iterate over, looking for the matching object
 * @param {Object} courseObject The course object to look for within the course progress array
 */
const _progressForObject = (courseProgress, courseObject) => {
  let match

  try {
    // LearnDash returns the object id in the `step` property
    const matches = courseProgress.filter(item => item.step === courseObject.id)
    match = matches.shift()
  } catch (error) {} // Swallow exceptions

  return match
}

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Retrieves the active user's progress for a given course
 * @param {Number} courseId The ID of the course for which to get progress
 * See https://developers.learndash.com/rest-api/v2/v2-user-course-progress-steps/ for more info.
 */
const getCourseProgress = async courseId => {
  // Try to get course progress, but fail silently
  // since we may not have a user yet
  let courseProgress = []
  try {
    const { id } = getAuthenticatedUser()
    const url = `${REMBLE_API_URL}/lms/`
    const steps = await learnDashGET(url, {
      uri: `/users/${id}/course-progress/${courseId}/steps`,
      no_cache: true
    })

    // Learndash returns a nested array for some reason???
    // So let's just return the first/only item in the nested array
    courseProgress = steps && steps.length ? steps[0] : steps
  } catch (error) {} // Swallow exceptions

  // Don't send an empty result
  return courseProgress ? courseProgress : []
}

/**
 * Retrieves the given user's progress for all courses or an empty array
 * See https://developers.learndash.com/rest-api/v2/v2-user-course-progress/ for more info.
 * @returns {Array} An array of course progress objects
 */
const getAllCourseProgress = async () => {
  // Try to get course progress, but fail silently
  // since we may not have a user yet
  let courseProgress = []
  try {
    const { id } = getAuthenticatedUser()
    const url = `${REMBLE_API_URL}/lms/`
    courseProgress = await learnDashGET(url, {
      uri: `/users/${id}/course-progress`,
      per_page: NO_LIMIT,
      no_cache: true
    })
  } catch (error) {} // Swallow exceptions

  // Don't send an empty result
  return courseProgress ? courseProgress : []
}

/**
 * Updates the user's course progress in a Topic, Lesson, Course, or Quiz given its ID and the Course ID.
 * See https://github.com/Remble/com.remble.www/blob/main/wordpress/wp-content/mu-plugins/remble-api.php for more info.
 * @param {Number} courseId The ID of the Course the object belongs to
 * @param {Object} courseObject The Course object (i.e., Topic, Lesson, Course, or Quiz) to update. Just needs to have a `type` and `id` property.
 * @param {String} action The action to perform. Should be 'update' or 'complete'
 */
const tryUpdateCourseProgressForCourseObject = async (
  courseId,
  courseObject,
  action = ACTION_UPDATE
) => {
  let result

  // Try to update course progress, but fail silently
  try {
    const { id } = getAuthenticatedUser()
    const url = `${REMBLE_WP_API_URL}/course-progress`
    const formData = new FormData()

    // Drop the LearnDash `sfwd-` prefix
    const objectType = courseObject.type.replace(LMS_TYPE_PREFIX, '')

    // Collect our field data
    const fields = {
      user_id: id,
      course_id: courseId,
      post_id: courseObject.id,
      activity_type: objectType,
      action
    }

    // Iterate over the fields and assign them to the form data
    for (const [key, value] of Object.entries(fields)) {
      formData.set(key, value)

      // Return early if we're missing any required fields
      if (!value) return
    }

    result = await _progressRESTPOST(url, { body: formData }).catch(error => {
      console.error('error', error)
      return { error }
    })
  } catch (error) {} // Swallow exceptions

  return result
}

/**
 * Tries to parse the course progress array and get a count of the completed topics for the lesson with the passed in ID
 * @param {Number} lessonId The ID of the lesson to lookup
 * @param {Array} courseProgress The progress data to iterate and search over
 * @param {Object} courseSteps The course steps object. See `getCourseSteps` or `getFullCourseById` in `Courses.js`
 * @returns The number of completed topics in the lesson or 0
 */
const tryGetTopicCompletedCountFromCourseProgress = (
  lessonId,
  courseProgress,
  courseSteps
) => {
  const topicData = tryGetTopicDataForLessonFromCourseSteps(
    lessonId,
    courseSteps
  )
  let count = 0

  // Return early if we didn't get any topic data
  if (!topicData) return count

  // Iterate through the progress array and see if we have any of these topics in it
  courseProgress
    .filter(item => item.post_type === LMS_TYPE_TOPIC)
    .forEach(item => {
      // Has this step been completed?
      const isCompleted = item.step_status === STATUS_COMPLETED
      const isInLesson = !!topicData[item.step]

      // If this item is in the lesson and it's complete, increment the counter
      if (isCompleted && isInLesson) count++

      return item
    })

  return count
}

/**
 * Iterates over the passed in progress data, looking for the passed in course object
 * Will return the value passed in for the `empty` value, if a result is not found
 * @param {Array} courseProgress The progress data to iterate and search over
 * @param {Object} courseObject The Object to look for within the progress data
 * @param {String} empty The default value to return if no result is found
 */
const tryGetStatusForCourseObject = (
  courseProgress,
  courseObject,
  empty = ''
) => {
  const match = _progressForObject(courseProgress, courseObject)

  return match ? match.step_status : empty
}

/**
 * Iterates over the passed in progress data, looking for the passed in course object
 * Will return a boolean indicating whether or not the object was found and was marked complete
 * @param {Array} courseProgress The progress data to iterate and search over
 * @param {Object} courseObject The Object (e.g., Lesson, Topic, Quiz, etc.) to look for within the progress data
 * @returns A boolean indicating whether `true` or not `false` the course item is complete
 */
const isComplete = (courseProgress, courseObject) => {
  const status = tryGetStatusForCourseObject(courseProgress, courseObject)

  return status === STATUS_COMPLETED
}

export {
  getCourseProgress,
  getAllCourseProgress,
  isComplete,
  tryGetStatusForCourseObject,
  tryGetTopicCompletedCountFromCourseProgress,
  tryUpdateCourseProgressForCourseObject,
  ACTION_COMPLETE,
  ACTION_UPDATE,
  STATUS_COMPLETED,
  STATUS_IN_PROGRESS,
  STATUS_STARTED
}
